import { api, ApiFailure } from './api.ts';
import { openContentsSubmit, openSubmit, selectionHumanCheck, type SubmitMode } from './submit.ts';
import type { PackageBatchResult, PackageDiscovery, PackageSelectionResult } from './types.ts';
import { KIND_NAME, esc, must, openModal, refresh } from './util.ts';
import { enableCoverFallbacks, packageCoverHtml } from './package-cover.ts';

type Source = { url: string; ref: string; path: string };

function listed(discovery: PackageDiscovery) {
  if (discovery.type === 'all') return { name: '内容', plural: 'packages', items: discovery.packages };
  if (discovery.type === 'skill') return { name: KIND_NAME.skill, plural: 'skills', items: discovery.skills };
  return { name: KIND_NAME.plugin, plural: 'plugins', items: discovery.plugins };
}

export function openPackageSelection(discovery: PackageDiscovery, mode: SubmitMode, source: Source, selected = new Set<string>(), results = new Map<string, PackageSelectionResult>()): void {
  const { name, plural, items } = listed(discovery);
  const verb = mode === 'admin' ? '添加' : '提交';
  if (items.length === 1 && results.size === 0) selected.add(items[0]!.path);
  openModal(`<div class="modal-head"><h2 id="selection-title">选择要${verb}的 ${name}</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
    <p class="modal-sub">找到 ${items.length} 个 ${name}，每次最多选择 ${discovery.maxSelection} 个。确认后会分别进行安全检测和审核。</p>
    <p class="selection-source">${esc(discovery.repo)} · ${esc(discovery.refLabel)} · 版本 ${esc(discovery.commit.slice(0, 7))}</p>
    ${items.length ? `<label class="field"><span>筛选 ${name}</span><input id="skill-filter" placeholder="按名称或目录查找"></label>
      <div class="selection-toolbar"><button class="btn btn-sm" id="select-all">全选</button><button class="btn btn-sm" id="select-none">清空选择</button><span id="selection-count" aria-live="polite"></span></div>
      <div class="skill-choices">${items.map((skill, index) => `<label class="skill-choice" data-index="${index}"><input type="checkbox" value="${esc(skill.path)}" ${selected.has(skill.path) ? 'checked' : ''}>
        ${packageCoverHtml(skill.name, skill.icon)}<span class="skill-choice-copy"><b>${esc(skill.name)}</b><small>${esc(skill.path || '仓库根目录')}</small></span></label>`).join('')}</div><p id="no-filter-results" class="dim" hidden>没有符合筛选条件的 ${name}。</p>`
      : `<div class="note info">没有找到 ${name}。请确认仓库和分支，或返回填写 ${name} 所在的目录。</div>`}
    <div id="selection-human"></div><div id="selection-message" role="status"></div>
    <div class="modal-actions"><button class="btn" id="selection-back">返回修改</button>${items.length ? `<button class="btn-primary" id="selection-submit">${verb}所选 ${name}</button>` : ''}</div>`);
  const modal = must('.modal');
  enableCoverFallbacks(modal);
  modal.classList.add('selection-dialog');
  modal.setAttribute('aria-labelledby', 'selection-title');
  must('#selection-back').onclick = () => (discovery.type === 'all' ? openContentsSubmit(mode, source) : openSubmit(discovery.type, mode, source));
  if (!items.length) return;
  const choices = [...modal.querySelectorAll<HTMLInputElement>('.skill-choice input')];
  const go = must<HTMLButtonElement>('#selection-submit');
  const message = must('#selection-message');
  let ready = mode === 'admin';
  let requiresToken = false;
  let busy = false;
  let token = '';
  let reset = () => undefined as void;
  const update = () => {
    selected = new Set(choices.filter(input => input.checked).map(input => input.value));
    must('#selection-count').textContent = `已选 ${selected.size} / ${discovery.maxSelection}`;
    go.disabled = busy || !ready || (requiresToken && !token) || selected.size === 0 || selected.size > discovery.maxSelection;
    if (selected.size > discovery.maxSelection) message.innerHTML = `<div class="note warn">每次最多选择 ${discovery.maxSelection} 个 ${name}，请取消部分选择。</div>`;
    else if (!busy) message.innerHTML = '';
  };
  choices.forEach(input => { input.onchange = update; });
  must('#select-all').onclick = () => { choices.filter(input => !input.closest<HTMLElement>('.skill-choice')!.hidden).forEach(input => { input.checked = true; }); update(); };
  must('#select-none').onclick = () => { choices.forEach(input => { input.checked = false; }); update(); };
  must<HTMLInputElement>('#skill-filter').oninput = event => {
    const query = (event.target as HTMLInputElement).value.trim().toLowerCase();
    let visible = 0;
    modal.querySelectorAll<HTMLElement>('.skill-choice').forEach(row => {
      const skill = items[Number(row.dataset.index)]!;
      row.hidden = !`${skill.name} ${skill.path}`.toLowerCase().includes(query);
      if (!row.hidden) visible += 1;
    });
    must('#no-filter-results').hidden = visible > 0;
    must('#select-all').textContent = query ? '全选筛选结果' : '全选';
  };
  update();
  if (mode === 'user') {
    void selectionHumanCheck(must('#selection-human'), value => { token = value; if (modal.isConnected) update(); })
      .then(check => { if (!modal.isConnected) return; reset = check.reset; requiresToken = check.required; ready = true; update(); })
      .catch(() => { if (modal.isConnected) message.innerHTML = '<div class="note err">人机验证设置暂时无法加载，请返回后重新查找。</div>'; });
  }
  go.onclick = async () => {
    if (go.disabled) return;
    busy = true;
    modal.dataset.busy = 'true';
    modal.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button,input').forEach(input => { input.disabled = true; });
    go.innerHTML = '<span class="spin"></span> 正在提交…';
    message.innerHTML = `<div class="note info">正在${verb} ${selected.size} 个 ${name}，请稍候。</div>`;
    try {
      const result = await api<PackageBatchResult>('POST', mode === 'admin' ? `/api/admin/${plural}/submit` : `/api/v1/${plural}/submit`, { discoveryId: discovery.discoveryId, paths: [...selected], ...(token ? { turnstileToken: token } : {}) });
      if (!modal.isConnected) return;
      result.results.forEach(row => results.set(row.path, row));
      modal.dataset.busy = 'false';
      showBatchResults(discovery, mode, source, results);
      refresh();
    } catch (error) {
      if (!modal.isConnected) return;
      busy = false;
      modal.dataset.busy = 'false';
      modal.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button,input').forEach(input => { input.disabled = false; });
      go.textContent = `${verb}所选 ${name}`;
      reset();
      update();
      const text = error instanceof ApiFailure ? error.message : '提交失败，请重试。';
      message.innerHTML = `<div class="note err">${esc(text)}</div>`;
      if (error instanceof ApiFailure && error.status === 410) { ready = false; go.disabled = true; must('#selection-back').textContent = '重新查找'; }
    }
  };
  must<HTMLInputElement>('#skill-filter').focus();
}

function showBatchResults(discovery: PackageDiscovery, mode: SubmitMode, source: Source, results: Map<string, PackageSelectionResult>): void {
  const { name, items } = listed(discovery);
  const rows = [...results.values()];
  const failed = rows.filter(row => row.status === 'failed');
  const count = (status: PackageSelectionResult['status']) => rows.filter(row => row.status === status).length;
  openModal(`<div class="modal-head"><h2 id="batch-title">${mode === 'admin' ? '添加' : '提交'}结果</h2><button class="x" data-act="close" aria-label="关闭">×</button></div>
    <p class="modal-sub">成功 ${count('submitted')} 个 · 已提交过 ${count('duplicate')} 个 · 失败 ${failed.length} 个。成功提交的 ${name} 会分别进行安全检测和管理员审核。</p>
    <div class="skill-results">${rows.map(row => `<div class="skill-result"><b>${esc(row.name || items.find(skill => skill.path === row.path)?.name)}</b>
      <span class="${row.status === 'failed' ? 'result-failed' : 'dim'}">${row.status === 'submitted' ? '已提交' : row.status === 'duplicate' ? '已提交过' : '提交失败'}</span>
      <small>${esc(row.path || '仓库根目录')}</small>${row.error ? `<p>${esc(row.error.message)}</p>` : ''}
      ${mode === 'admin' && row.id ? `<a class="link" href="#/admin/review/${esc(row.id)}">查看检测与审核</a>` : ''}</div>`).join('')}</div>
    ${failed.some(row => !row.error?.retryable) ? `<p class="dim">需要修改仓库内容时，请修改后重新查找 ${name}，以提交新的版本。</p>` : ''}
    <div class="modal-actions selection-result-actions"><button class="btn" id="batch-source">重新查找</button><button class="btn" id="batch-back">${failed.length ? '选择失败项重试' : '继续选择'}</button><button class="btn-primary" data-act="close">完成</button></div>`);
  must('.modal').setAttribute('aria-labelledby', 'batch-title');
  must('.modal').classList.add('selection-dialog');
  must('#batch-source').onclick = () => (discovery.type === 'all' ? openContentsSubmit(mode, source) : openSubmit(discovery.type, mode, source));
  must('#batch-back').onclick = () => openPackageSelection(discovery, mode, source, new Set(failed.map(row => row.path)), results);
}
